import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ChevronRightIcon, PaperclipIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { foodTruckApplication, foodTruckDays, foodTruckDocuments, foodTruckMenuTypes, foodTruckZones } from '../data/permis';
import { usePermits } from '../contexts/PermitsContext';
type Step = 0 | 1 | 2 | 3;
const steps = [{
  id: 0,
  label: 'Demandeur'
}, {
  id: 1,
  label: 'Entreprise et véhicule'
}, {
  id: 2,
  label: 'Emplacements et horaire'
}, {
  id: 3,
  label: 'Documents et déclaration'
}] as const;
const fieldClass = 'mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus:ring-2 focus:ring-ville-indigo/20';
const labelClass = 'block text-[13px] font-bold text-ville-navy';
const requiredMark = <span className="text-ville-coral" data-id="element-3187" data-mp="uwc1gbythdan"> *</span>;
function Field({
  id,
  label,
  required,
  hint,
  children
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return <p data-id="element-3188" data-mp="ktewqiuq2t80">
      <label htmlFor={id} className={labelClass} data-id="element-3189" data-mp="62pit8pvn00g">
        {label}
        {required && requiredMark}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500" data-id="element-3190" data-mp="b2kctl4y85pq">{hint}</span>}
    </p>;
}
export function PermisFormulaire() {
  const navigate = useNavigate();
  const {
    addPermit
  } = usePermits();
  const [step, setStep] = useState<Step>(0);
  const [zones, setZones] = useState<string[]>(['cite']);
  const [days, setDays] = useState<string[]>(['Jeudi', 'Vendredi', 'Samedi', 'Dimanche']);
  const [documents, setDocuments] = useState<string[]>([]);
  const [declared, setDeclared] = useState(false);
  const [sent, setSent] = useState(false);
  const toggle = (list: string[], value: string, set: (next: string[]) => void) => set(list.includes(value) ? list.filter(item => item !== value) : [...list, value]);
  const submit = () => {
    addPermit(foodTruckApplication);
    setSent(true);
  };
  if (sent) {
    return <main className="bg-white" data-id="element-3191" data-mp="1g1h7wp6j7tzl">
        <div className="mx-auto max-w-[760px] px-5 py-16 lg:px-10" data-id="element-3192" data-mp="1b9pgkcfjxk8e">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-3193" data-mp="1b7w7yrldopxr">
            <CheckIcon className="h-7 w-7" data-id="element-3194" data-mp="11cuifun6xlun" />
          </span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ville-navy" data-id="element-3195" data-mp="xa7o9n31roua">
            Formulaire transmis à la Ville
          </h1>
          <p className="mt-2 text-[17px] text-slate-600" data-id="element-3196" data-mp="sbwcv7x3t69y">
            Votre demande de permis d’exploitation d’un véhicule de restauration porte le numéro{' '}
            {foodTruckApplication.reference}. L’analyse prend {foodTruckApplication.processingTime}. Les frais de{' '}
            {foodTruckApplication.fee} seront exigibles seulement si la demande est acceptée.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-3197" data-mp="2pac0cejmttcc">
            <Link to={`/mes-permis/${foodTruckApplication.id}`} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3198" data-mp="2u8nakqdua5v3">
              Suivre ma demande
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3199" data-mp="2b99g87c76t42" />
            </Link>
            <Link to="/mes-permis" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3200" data-mp="2g95hvzxfa8gg">
              Retour à mes permis
            </Link>
          </div>
        </div>
      </main>;
  }
  return <main className="bg-white" data-id="element-3201" data-mp="3uaa9zx1fwm8o">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-3202" data-mp="3q7nftphaqp8b">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-3203" data-mp="8193tw2l8pck">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3204" data-mp="31mq7liul738">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3205" data-mp="h0vx1ul5youj" />
          <Link to="/mes-permis" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-3206" data-mp="d0ls8l93y4h9">
            Mes permis
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3207" data-mp="qqfh33km4oqz" />
          <span aria-current="page" data-id="element-3208" data-mp="ls5l4qr2dk4b">Formulaire</span>
        </nav>

        <header className="mt-4" data-id="element-3209" data-mp="lv040dufiwvm">
          <p className="text-sm font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-3210" data-mp="10p4ykv8lk76p">
            Formulaire U-214 · Urbanisme et permis
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-ville-navy lg:text-4xl" data-id="element-3211" data-mp="voqqxeawqz97">
            Permis d’exploitation d’un véhicule de restauration
          </h1>
          <p className="mt-2 max-w-2xl text-[17px] text-slate-600" data-id="element-3212" data-mp="1jdoxtx54spor">
            Commerce mobile sur le domaine public. Frais de {foodTruckApplication.fee}, payables uniquement si
            la demande est acceptée. Délai d’analyse : {foodTruckApplication.processingTime}.
          </p>
          <p className="mt-3 flex items-start gap-2 rounded-2xl bg-ville-indigo-soft px-4 py-3 text-sm text-ville-navy" data-id="element-3213" data-mp="vnuldgoq5351">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-3214" data-mp="10gnra7vxihip" />
            ATNA peut remplir ce formulaire pour vous en quelques questions.{' '}
            <Link to="/mes-permis" className="font-bold text-ville-indigo hover:underline" data-id="element-3215" data-mp="rnkd1xdgm0n0">
              Utiliser ATNA
            </Link>
          </p>
        </header>

        <ol className="mt-8 flex flex-wrap items-center gap-2 text-xs font-bold" data-id="element-3216" data-mp="hp4ljtaispcz">
          {steps.map((item, index) => {
          const done = step > index;
          const active = step === index;
          return <li key={item.id} className="flex items-center gap-2" data-id="element-3217" data-mp="3pxcma23ams6m" data-mpi={index}>
                <span className={`rounded-full px-3 py-1.5 ${active ? 'bg-ville-navy text-white' : done ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-500'}`} data-id="element-3218" data-mp="3kz1ctsgwujox">
                  {index + 1}. {item.label}
                </span>
                {index < steps.length - 1 && <span className="h-px w-4 bg-slate-200" aria-hidden="true" data-id="element-3219" data-mp="7j3cpeqdjxdf" />}
              </li>;
        })}
        </ol>

        <form className="mt-8 rounded-3xl border border-slate-200 p-6 sm:p-8" onSubmit={event => {
        event.preventDefault();
        if (step < 3) {
          setStep(current => current + 1 as Step);
          return;
        }
        if (declared) submit();
      }} data-id="element-3220" data-mp="2sb5iuviq75vu">
          {step === 0 && <fieldset data-id="element-3221" data-mp="17te6bnhfkhsv">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3222" data-mp="17qrwtiazbs80">
                Section 1 — Identification du demandeur
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3223" data-mp="1hkiikffq1ok0">Les champs marqués d’un astérisque sont obligatoires.</p>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3224" data-mp="1mk3k8xma0b0f">
                <Field id="nom" label="Nom" required data-id="element-3225" data-mp="1qikuvr4wo3ml">
                  <input id="nom" defaultValue="Bouchard" className={fieldClass} data-id="element-3226" data-mp="1vipa7du2usb8" />
                </Field>
                <Field id="prenom" label="Prénom" required data-id="element-3227" data-mp="20hs7ud7q9w96">
                  <input id="prenom" defaultValue="Charlotte" className={fieldClass} data-id="element-3228" data-mp="20h0l7rw3z8v1" />
                </Field>
                <Field id="courriel" label="Courriel" required data-id="element-3229" data-mp="16sfnc0c96by">
                  <input id="courriel" type="email" defaultValue="charlotte.bouchard@courriel.com" className={fieldClass} data-id="element-3230" data-mp="64u0a1dexdpj" />
                </Field>
                <Field id="telephone" label="Téléphone" required data-id="element-3231" data-mp="230do9f8g341d">
                  <input id="telephone" type="tel" defaultValue="450 555-0184" className={fieldClass} data-id="element-3232" data-mp="2hqkzb9w4hks1" />
                </Field>
                <Field id="adresse" label="Adresse du demandeur" required data-id="element-3233" data-mp="2cudq988fjt5v">
                  <input id="adresse" defaultValue="4590, rue Bourgogne" className={fieldClass} data-id="element-3234" data-mp="1p2ytduzfa420" />
                </Field>
                <Field id="postal" label="Code postal" required data-id="element-3235" data-mp="1k5ghpwu0taqb">
                  <input id="postal" defaultValue="J4W 2K1" className={fieldClass} data-id="element-3236" data-mp="1z1eb57ck80yu" />
                </Field>
                <Field id="qualite" label="À quel titre faites-vous la demande ?" required data-id="element-3237" data-mp="1tzys1kty04j3">
                  <select id="qualite" defaultValue="proprietaire" className={fieldClass} data-id="element-3238" data-mp="3aegw0elozcot">
                    <option value="proprietaire" data-id="element-3239" data-mp="35fx47xkf36oj">Propriétaire de l’entreprise</option>
                    <option value="mandataire" data-id="element-3240" data-mp="3czy80yasax2a">Mandataire</option>
                    <option value="organisme" data-id="element-3241" data-mp="3hzaxzrwyck93">Organisme à but non lucratif</option>
                  </select>
                </Field>
                <Field id="residence" label="Résident de Brossard" required data-id="element-3242" data-mp="340b5282epj74">
                  <select id="residence" defaultValue="oui" className={fieldClass} data-id="element-3243" data-mp="380v81s9nvh16">
                    <option value="oui" data-id="element-3244" data-mp="3vqpf1t7aubdi">Oui</option>
                    <option value="non" data-id="element-3245" data-mp="3vs0itt0qvj4m">Non</option>
                  </select>
                </Field>
              </div>
            </fieldset>}

          {step === 1 && <fieldset data-id="element-3246" data-mp="3mxcfw9in6diz">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3247" data-mp="3mxvw9lsn8tuw">
                Section 2 — Entreprise et véhicule
              </legend>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3248" data-mp="ijxh2bg8tvrm">
                <Field id="entreprise" label="Nom de l’entreprise" required data-id="element-3249" data-mp="iiclqjm3u8sp">
                  <input id="entreprise" placeholder="Ex. Cantine Bourgogne" className={fieldClass} data-id="element-3250" data-mp="nc751lxsslr9" />
                </Field>
                <Field id="neq" label="Numéro d’entreprise (NEQ)" required hint="10 chiffres, sans espace" data-id="element-3251" data-mp="1orl494qr2aua">
                  <input id="neq" inputMode="numeric" placeholder="1170000000" className={fieldClass} data-id="element-3252" data-mp="19ukn1v66pya2" />
                </Field>
                <Field id="menu" label="Type de menu" required data-id="element-3253" data-mp="1esm3078bdhhb">
                  <select id="menu" defaultValue={foodTruckMenuTypes[0]} className={fieldClass} data-id="element-3254" data-mp="1120mgp6c1jml">
                    {foodTruckMenuTypes.map(type => <option key={type} value={type} data-id="element-3255" data-mp="15ua6xpoohrhh">
                        {type}
                      </option>)}
                  </select>
                </Field>
                <Field id="mapaq" label="Numéro de permis du MAPAQ" required data-id="element-3256" data-mp="w3pb2a0e3129">
                  <input id="mapaq" placeholder="MAPAQ-000000" className={fieldClass} data-id="element-3257" data-mp="i2wd1favpnue" />
                </Field>
                <Field id="plaque" label="Plaque d’immatriculation" required data-id="element-3258" data-mp="m3oma8jr3sqo">
                  <input id="plaque" placeholder="F00 000" className={fieldClass} data-id="element-3259" data-mp="3fxn5oro71hs" />
                </Field>
                <Field id="longueur" label="Longueur du véhicule (m)" required data-id="element-3260" data-mp="3unikc4jz30d4">
                  <input id="longueur" type="number" step="0.1" placeholder="7,3" className={fieldClass} data-id="element-3261" data-mp="cgvvukd1zx6p" />
                </Field>
                <Field id="cuisson" label="Mode de cuisson" required data-id="element-3262" data-mp="7gzocb55dnme">
                  <select id="cuisson" defaultValue="propane" className={fieldClass} data-id="element-3263" data-mp="mf1w6xj7ov9g">
                    <option value="propane" data-id="element-3264" data-mp="hg8esu0t0k40">Propane</option>
                    <option value="electrique" data-id="element-3265" data-mp="hfp8kakink5t">Électrique</option>
                    <option value="mixte" data-id="element-3266" data-mp="v7vcmyaea0ow">Mixte</option>
                  </select>
                </Field>
                <Field id="generatrice" label="Génératrice" required hint="Le niveau sonore doit rester sous 65 dB" data-id="element-3267" data-mp="r5s34f7ad669">
                  <select id="generatrice" defaultValue="oui" className={fieldClass} data-id="element-3268" data-mp="156kyf25ew5af">
                    <option value="oui" data-id="element-3269" data-mp="107rh0yn07u4z">Oui</option>
                    <option value="non" data-id="element-3270" data-mp="104vmzlea1plt">Non</option>
                  </select>
                </Field>
              </div>

              <Field id="description" label="Description de l’exploitation" required hint="Nombre d’employés, gestion des déchets, approvisionnement en eau." data-id="element-3271" data-mp="1udvavjc9rgh">
                <textarea id="description" rows={4} placeholder="Décrivez votre exploitation…" className={`${fieldClass} resize-y`} data-id="element-3272" data-mp="bm9u68t91llr" />
              </Field>
            </fieldset>}

          {step === 2 && <fieldset data-id="element-3273" data-mp="pkzqvnc5pj9l">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3274" data-mp="pinctl9irt1t">
                Section 3 — Emplacements et horaire demandés
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3275" data-mp="2rde78hz1yfnw">
                Seules les zones autorisées par le règlement ZON-2021-04 peuvent être demandées.
              </p>

              <fieldset className="mt-6" data-id="element-3276" data-mp="2osgiluidqbvu">
                <legend className={labelClass} data-id="element-3277" data-mp="2othw05mvbagq">Zones demandées{requiredMark}</legend>
                <ul className="mt-2 space-y-2" data-id="element-3278" data-mp="2jzdmbem7woby">
                  {foodTruckZones.map(zone => <li key={zone.id} data-id="element-3279" data-mp="2f1vc7kwgs32d">
                      <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-3280" data-mp="2a2sigwlxpj9p">
                        <input type="checkbox" checked={zones.includes(zone.id)} onChange={() => toggle(zones, zone.id, setZones)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-3281" data-mp="37qssjih47psh" />
                        <span className="min-w-0" data-id="element-3282" data-mp="2tr9lyu3ycmia">
                          <span className="block text-sm font-bold text-ville-navy" data-id="element-3283" data-mp="2tsko6phr1m7a">{zone.label}</span>
                          <span className="block text-[13px] text-slate-500" data-id="element-3284" data-mp="1maldj92583yo">{zone.detail}</span>
                        </span>
                      </label>
                    </li>)}
                </ul>
              </fieldset>

              <fieldset className="mt-6" data-id="element-3285" data-mp="3az0r5i962lxs">
                <legend className={labelClass} data-id="element-3286" data-mp="3b0u239qtbsbl">Jours d’exploitation{requiredMark}</legend>
                <ul className="mt-2 flex flex-wrap gap-2" data-id="element-3287" data-mp="2wbo4trbjcc6c">
                  {foodTruckDays.map(day => {
                const active = days.includes(day);
                return <li key={day} data-id="element-3288" data-mp="3oz1tl252y1fd">
                        <button type="button" aria-pressed={active} onClick={() => toggle(days, day, setDays)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'bg-ville-navy text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo'}`} data-id="element-3289" data-mp="3txlf51frhb77">
                          {day}
                        </button>
                      </li>;
              })}
                </ul>
              </fieldset>

              <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-3290" data-mp="25zazg62c9oji">
                <Field id="debut" label="Début de l’exploitation" required data-id="element-3291" data-mp="25xrfo5hh4vcz">
                  <input id="debut" type="date" defaultValue="2026-10-01" className={fieldClass} data-id="element-3292" data-mp="1iarofirwmxnq" />
                </Field>
                <Field id="fin" label="Fin de l’exploitation" required data-id="element-3293" data-mp="1d92g4aw2gy3c">
                  <input id="fin" type="date" defaultValue="2027-05-31" className={fieldClass} data-id="element-3294" data-mp="1s1me6x4d0fdx" />
                </Field>
                <Field id="heure-debut" label="Heure d’ouverture" required data-id="element-3295" data-mp="1n79r8d3oclxf">
                  <input id="heure-debut" type="time" defaultValue="11:00" className={fieldClass} data-id="element-3296" data-mp="zi7cbd0fd76m" />
                </Field>
                <Field id="heure-fin" label="Heure de fermeture" required hint="Maximum 22 h en zone résidentielle" data-id="element-3297" data-mp="uikyb1crvmwr">
                  <input id="heure-fin" type="time" defaultValue="20:00" className={fieldClass} data-id="element-3298" data-mp="1oaelsingg0wb" />
                </Field>
              </div>
            </fieldset>}

          {step === 3 && <fieldset data-id="element-3299" data-mp="1y07vfe41e5xx">
              <legend className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-3300" data-mp="1y0hd8eexh6sj">
                Section 4 — Documents et déclaration
              </legend>
              <p className="mt-1 text-sm text-slate-500" data-id="element-3301" data-mp="11cbbbz9e4shy">
                Formats acceptés : PDF, JPG ou PNG, maximum 10 Mo par fichier.
              </p>

              <ul className="mt-6 space-y-2" data-id="element-3302" data-mp="1a9vjzt9l1r2l">
                {foodTruckDocuments.map(document => {
              const attached = documents.includes(document.id);
              return <li key={document.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-id="element-3303" data-mp="2lp0p13x6s1pd">
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${attached ? 'bg-ville-mist text-ville-deep' : 'bg-slate-200 text-slate-500'}`} aria-hidden="true" data-id="element-3304" data-mp="f08gm3yxljgb">
                        <CheckIcon className="h-3 w-3" data-id="element-3305" data-mp="cdmqk96t91n" />
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-3306" data-mp="3si0xdjtphbq3">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-3307" data-mp="3ngtrjqb4imm2">
                          {document.name}
                          {requiredMark}
                        </span>
                        <span className="block text-[13px] text-slate-500" data-id="element-3308" data-mp="3djp1vgi2k90i">{document.detail}</span>
                      </span>
                      <button type="button" onClick={() => toggle(documents, document.id, setDocuments)} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3309" data-mp="1gpu72l5uume1">
                        <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-3310" data-mp="fu6r0d6fse1n" />
                        {attached ? 'Remplacer' : 'Joindre'}
                      </button>
                    </li>;
            })}
              </ul>

              <label className="mt-6 flex items-start gap-3 rounded-2xl bg-ville-sand px-4 py-4" data-id="element-3311" data-mp="3p0y01gcrp028">
                <input type="checkbox" checked={declared} onChange={event => setDeclared(event.target.checked)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-3312" data-mp="3t202e61zds8f" />
                <span className="text-sm leading-relaxed text-slate-700" data-id="element-3313" data-mp="1rad89vmslh7q">
                  Je déclare que les renseignements fournis sont exacts et m’engage à respecter le règlement sur
                  les commerces mobiles ainsi que les conditions du permis. Toute fausse déclaration peut
                  entraîner la révocation du permis.
                  {requiredMark}
                </span>
              </label>

              <p className="mt-4 text-[13px] text-slate-500" data-id="element-3314" data-mp="3gpxrdxtaz1bi">
                Aucun paiement n’est requis à l’envoi. Les frais de {foodTruckApplication.fee} vous seront
                réclamés uniquement si la Ville accepte la demande.
              </p>
            </fieldset>}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6" data-id="element-3315" data-mp="8bfk3yb6rpsy">
            <button type="button" onClick={() => step === 0 ? navigate('/mes-permis') : setStep(current => current - 1 as Step)} className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-3316" data-mp="3bb6cg1nx96f">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-3317" data-mp="18f47rjcyej2q" />
              {step === 0 ? 'Annuler' : 'Étape précédente'}
            </button>

            {step < 3 ? <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-3318" data-mp="1i7jpqgo937nm">
                Étape suivante
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3319" data-mp="yhg23ydt7r5s" />
              </button> : <button type="submit" disabled={!declared} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" data-id="element-3320" data-mp="2ez3ue56d5x66">
                <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-3321" data-mp="2dgdll2s4clqp" />
                Transmettre ma demande
              </button>}
          </div>
        </form>
      </div>
    </main>;
}